import React, { useState, useEffect, useCallback, memo } from 'react';
import {
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  InputLabel,
  FormControl,
  List,
  ListItem,
  ListItemText,
  Divider,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { motion, AnimatePresence } from 'framer-motion';

// Styled components
const SectionTitle = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(2),
  fontWeight: 600,
  color: '#1e293b',
  fontSize: '1.6rem',
  lineHeight: 1.3,
  position: 'relative',
  '&:before': {
    content: '""',
    position: 'absolute',
    left: -16,
    top: 0,
    height: '100%',
    width: 4,
    background: 'linear-gradient(to bottom, #14b8a6, #0d9488)',
  },
  fontFamily: "'Inter', sans-serif",
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  borderRadius: 12,
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(8px)',
  border: '1px solid rgba(15, 23, 42, 0.1)',
  transition: 'all 0.3s ease',
  fontFamily: "'Inter', sans-serif",
  '& .MuiSelect-select': {
    padding: '16px 20px',
    fontSize: '1rem',
    color: '#1e293b',
    fontFamily: "'Inter', sans-serif",
    display: 'flex',
    alignItems: 'center',
  },
  '&:hover': {
    borderColor: '#14b8a6',
    boxShadow: '0 4px 12px rgba(20, 184, 166, 0.2)',
  },
  '&.Mui-focused': {
    borderColor: '#14b8a6',
    boxShadow: '0 4px 16px rgba(20, 184, 166, 0.3)',
  },
  height: '56px',
  width: '100%',
}));

const StyledButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.5, 4),
  fontWeight: 600,
  borderRadius: 12,
  background: 'linear-gradient(45deg, #14b8a6 30%, #0d9488 90%)',
  color: '#fff',
  textTransform: 'none',
  fontSize: '0.95rem',
  fontFamily: "'Inter', sans-serif",
  transition: 'all 0.3s ease',
  boxShadow: '0 4px 12px rgba(20, 184, 166, 0.3)',
  '&:hover': {
    background: 'linear-gradient(45deg, #0d9488 30%, #14b8a6 90%)',
    boxShadow: '0 8px 24px rgba(20, 184, 166, 0.5)',
    transform: 'scale(1.05)',
  },
  '&:disabled': {
    background: '#e2e8f0',
    color: '#94a3b8',
    boxShadow: 'none',
  },
}));

const IconOnlyButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.5),
  minWidth: '48px',
  borderRadius: 12,
  background: 'linear-gradient(45deg, #f43f5e 30%, #e11d48 90%)',
  color: '#fff',
  transition: 'all 0.3s ease',
  boxShadow: '0 4px 12px rgba(244, 63, 94, 0.3)',
  '&:hover': {
    background: 'linear-gradient(45deg, #e11d48 30%, #f43f5e 90%)',
    boxShadow: '0 8px 24px rgba(244, 63, 94, 0.5)',
    transform: 'scale(1.05)',
  },
  '&:disabled': {
    background: '#e2e8f0',
    color: '#94a3b8',
    boxShadow: 'none',
  },
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));

const StyledList = styled(List)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(12px)',
  borderRadius: 16,
  boxShadow: '0 8px 32px rgba(15, 23, 42, 0.1)',
  overflow: 'hidden',
  fontFamily: "'Inter', sans-serif",
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(2.5, 3),
  transition: 'all 0.3s ease',
  borderLeft: '4px solid transparent',
  '&:hover': {
    background: 'rgba(20, 184, 166, 0.05)',
    borderLeftColor: '#14b8a6',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 16px rgba(15, 23, 42, 0.1)',
  },
  fontFamily: "'Inter', sans-serif",
}));

const ListItemPrimaryText = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: '#1e293b',
  fontSize: '1.1rem',
  fontFamily: "'Inter', sans-serif",
}));

const ActionButton = styled(Button)(({ theme }) => ({
  fontWeight: 600,
  borderRadius: 8,
  textTransform: 'none',
  padding: '8px 16px',
  fontSize: '0.9rem',
  transition: 'all 0.3s ease',
  boxShadow: '0 4px 12px rgba(15, 23, 42, 0.1)',
  fontFamily: "'Inter', sans-serif",
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0 6px 16px rgba(15, 23, 42, 0.2)',
  },
}));

const StyledDivider = styled(Divider)(({ theme }) => ({
  background: 'rgba(15, 23, 42, 0.1)',
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 20,
    background: 'rgba(255, 255, 255, 0.98)',
    backdropFilter: 'blur(12px)',
    boxShadow: '0 8px 32px rgba(15, 23, 42, 0.2)',
    maxWidth: 600,
    width: '100%',
    overflow: 'hidden',
    fontFamily: "'Inter', sans-serif",
    border: '1px solid rgba(20, 184, 166, 0.1)',
  },
}));

const DialogHeader = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(45deg, #14b8a6 30%, #0d9488 90%)',
  padding: theme.spacing(2.5),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  color: '#fff',
  fontFamily: "'Inter', sans-serif",
  boxShadow: '0 4px 12px rgba(15, 23, 42, 0.1)',
}));

const DialogTitleText = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '1.4rem',
  fontFamily: "'Inter', sans-serif",
  letterSpacing: '0.5px',
}));

const DialogCloseButton = styled(IconButton)(({ theme }) => ({
  color: '#fff',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.2)',
    transform: 'rotate(90deg)',
  },
}));

const CategoriesSection = ({ restaurantId }) => {
  const [categories, setCategories] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [categoryForm, setCategoryForm] = useState({ id_filtre: '' });
  const [categoryErrors, setCategoryErrors] = useState({});
  const [categoryEditId, setCategoryEditId] = useState(null);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [categoryError, setCategoryError] = useState('');
  const [categorySuccess, setCategorySuccess] = useState('');
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [actionLoading, setActionLoading] = useState({});

  const fetchCategories = useCallback(async () => {
    setCategoryLoading(true);
    try {
      const [categoriesResponse, availableResponse] = await Promise.all([
        axios.get(`https://apibackoffice.lalivraison.ma/api/restaurants/${restaurantId}/categories`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        }),
        axios.get(`https://apibackoffice.lalivraison.ma/api/restaurants/categories/available`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        }),
      ]);
      console.log('Categories Response:', categoriesResponse.data);
      console.log('Available Categories Response:', availableResponse.data);

      const validCategories = categoriesResponse.data.filter(
        (category) => category && typeof category === 'object' && category.id
      );
      setCategories(validCategories);
      setAvailableCategories(availableResponse.data || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setCategoryError(err.response?.data?.message || 'Erreur lors de la récupération des catégories.');
    } finally {
      setCategoryLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleCategoryChange = useCallback((e) => {
    const { name, value } = e.target;
    setCategoryForm((prev) => ({ ...prev, [name]: value }));
    setCategoryErrors((prev) => ({ ...prev, [name]: '' }));
  }, []);

  const validateCategoryForm = useCallback(() => {
    const errors = {};
    if (!categoryForm.id_filtre) errors.id_filtre = 'La catégorie est requise.';
    setCategoryErrors(errors);
    return Object.keys(errors).length === 0;
  }, [categoryForm]);

  const openCategoryForm = useCallback((category = null) => {
    if (category) {
      setCategoryForm({ id_filtre: category.id || '' });
      setCategoryEditId(category.id || null);
    } else {
      setCategoryForm({ id_filtre: '' });
      setCategoryEditId(null);
    }
    setCategoryErrors({});
    setOpenCategoryDialog(true);
  }, []);

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    if (!validateCategoryForm()) return;

    setActionLoading((prev) => ({ ...prev, submitCategory: true }));
    setCategoryError('');
    setCategorySuccess('');

    try {
      const data = { id_filtre: parseInt(categoryForm.id_filtre, 10) };

      let response;
      if (categoryEditId) {
        response = await axios.put(
          `https://apibackoffice.lalivraison.ma/api/restaurants/${restaurantId}/categories/${categoryEditId}`,
          data,
          {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          }
        );
        console.log('Updated Category Response:', response.data);
        setCategories((prev) =>
          prev.map((c) => (c.id === categoryEditId ? response.data.category : c))
        );
        setCategorySuccess('Catégorie mise à jour avec succès !');
      } else {
        response = await axios.post(
          `https://apibackoffice.lalivraison.ma/api/restaurants/${restaurantId}/categories`,
          data,
          {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          }
        );
        console.log('Added Category Response:', response.data);
        // Ensure the new category has the correct structure
        const newCategory = {
          id: response.data.category?.id || data.id_filtre,
          name: availableCategories.find((c) => c.id === data.id_filtre)?.name || 'Inconnu',
        };
        setCategories((prev) => [...prev, newCategory]);
        setCategorySuccess('Catégorie ajoutée avec succès !');
      }
      setOpenCategoryDialog(false);
      // Refresh categories to ensure consistency with server
      await fetchCategories();
    } catch (err) {
      console.error('Error submitting category:', err.response?.data || err.message);
      setCategoryError(err.response?.data?.message || 'Erreur lors de la gestion de la catégorie.');
    } finally {
      setActionLoading((prev) => ({ ...prev, submitCategory: false }));
    }
  };

  const handleCategoryDelete = async (categoryId) => {
    if (!window.confirm('Voulez-vous vraiment supprimer cette catégorie ?')) return;
    setActionLoading((prev) => ({ ...prev, [`delete_${categoryId}`]: true }));
    setCategoryError('');
    setCategorySuccess('');

    try {
      console.log('Deleting category with ID:', categoryId);
      await axios.delete(`https://apibackoffice.lalivraison.ma/api/restaurants/${restaurantId}/categories/${categoryId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setCategories((prev) => prev.filter((c) => c.id !== categoryId));
      setCategorySuccess('Catégorie supprimée avec succès !');
      // Refresh categories to ensure consistency with server
      await fetchCategories();
    } catch (err) {
      console.error('Error deleting category:', err.response?.data || err.message);
      setCategoryError(err.response?.data?.message || 'Erreur lors de la suppression de la catégorie.');
    } finally {
      setActionLoading((prev) => ({ ...prev, [`delete_${categoryId}`]: false }));
    }
  };

  const handleDeleteAllCategories = async () => {
    if (!window.confirm('Voulez-vous vraiment supprimer toutes les catégories ? Cette action est irréversible.')) return;
    setActionLoading((prev) => ({ ...prev, deleteAll: true }));
    setCategoryError('');
    setCategorySuccess('');

    try {
      await axios.delete(`https://apibackoffice.lalivraison.ma/api/restaurants/${restaurantId}/categories`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setCategories([]);
      setCategorySuccess('Toutes les catégories ont été supprimées avec succès !');
      await fetchCategories();
    } catch (err) {
      console.error('Error deleting all categories:', err.response?.data || err.message);
      setCategoryError(err.response?.data?.message || 'Erreur lors de la suppression de toutes les catégories.');
    } finally {
      setActionLoading((prev) => ({ ...prev, deleteAll: false }));
    }
  };

  const handleCloseSnackbar = () => {
    setCategoryError('');
    setCategorySuccess('');
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <SectionTitle variant="h6">Catégories</SectionTitle>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <StyledButton
            onClick={() => openCategoryForm()}
            disabled={categoryLoading}
            startIcon={<AddIcon />}
          >
            Ajouter une Catégorie
          </StyledButton>
          <Tooltip title="Supprimer toutes les catégories">
            <IconOnlyButton
              onClick={handleDeleteAllCategories}
              disabled={actionLoading.deleteAll || categoryLoading || categories.length === 0}
              aria-label="Supprimer toutes les catégories"
            >
              {actionLoading.deleteAll ? (
                <CircularProgress size={24} sx={{ color: '#fff' }} />
              ) : (
                <DeleteIcon />
              )}
            </IconOnlyButton>
          </Tooltip>
        </Box>
      </Box>

      {categoryLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress size={40} sx={{ color: '#14b8a6' }} />
        </Box>
      )}
      {categoryError && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 12, fontFamily: "'Inter', sans-serif" }}>
          {categoryError}
        </Alert>
      )}
      {categorySuccess && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 12, fontFamily: "'Inter', sans-serif" }}>
          {categorySuccess}
        </Alert>
      )}
      {categories.length === 0 && !categoryLoading && (
        <Typography sx={{ textAlign: 'center', color: '#64748b', py: 4, fontSize: '1.1rem', fontFamily: "'Inter', sans-serif" }}>
          Aucune catégorie trouvée. Ajoutez-en une pour commencer.
        </Typography>
      )}
      {categories.length > 0 && (
        <StyledList>
          <AnimatePresence>
            {categories.map((category, index) => {
              if (!category || !category.id) {
                console.warn('Invalid category detected:', category);
                return null;
              }
              const categoryDetails = availableCategories.find((c) => c.id === category.id) || { name: 'Inconnu' };
              return (
                <motion.div
                  key={category.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <StyledListItem>
                    <ListItemText
                      primary={<ListItemPrimaryText>{categoryDetails.name}</ListItemPrimaryText>}
                    />
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      <ActionButton
                        variant="contained"
                        sx={{
                          bgcolor: '#14b8a6',
                          color: '#fff',
                          '&:hover': { bgcolor: '#0d9488' },
                        }}
                        onClick={() => openCategoryForm(category)}
                        startIcon={<EditIcon />}
                        disabled={categoryLoading}
                      >
                        Modifier
                      </ActionButton>
                      <ActionButton
                        variant="contained"
                        sx={{
                          bgcolor: '#f43f5e',
                          color: '#fff',
                          '&:hover': { bgcolor: '#e11d48' },
                        }}
                        onClick={() => handleCategoryDelete(category.id)}
                        startIcon={actionLoading[`delete_${category.id}`] ? null : <DeleteIcon />}
                        disabled={actionLoading[`delete_${category.id}`] || categoryLoading}
                      >
                        {actionLoading[`delete_${category.id}`] ? (
                          <CircularProgress size={24} sx={{ color: '#fff' }} />
                        ) : (
                          'Supprimer'
                        )}
                      </ActionButton>
                    </Box>
                  </StyledListItem>
                  {index < categories.length - 1 && <StyledDivider />}
                </motion.div>
              );
            })}
          </AnimatePresence>
        </StyledList>
      )}

      <StyledDialog
        open={openCategoryDialog}
        onClose={() => setOpenCategoryDialog(false)}
      >
        <DialogHeader>
          <DialogTitleText>
            {categoryEditId ? 'Modifier la Catégorie' : 'Ajouter une Catégorie'}
          </DialogTitleText>
          <DialogCloseButton onClick={() => setOpenCategoryDialog(false)}>
            <CloseIcon />
          </DialogCloseButton>
        </DialogHeader>
        <DialogContent sx={{ p: 4, background: 'linear-gradient(145deg, #f8fafc, #ffffff)' }}>
          <Box component="form" onSubmit={handleCategorySubmit}>
            <FormControl fullWidth error={!!categoryErrors.id_filtre}>
              <InputLabel
                id="category-label"
                sx={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: '1rem',
                  color: '#64748b',
                  '&.Mui-focused': { color: '#14b8a6' },
                }}
              >
                Catégorie
              </InputLabel>
              <StyledSelect
                labelId="category-label"
                label="Catégorie"
                name="id_filtre"
                value={categoryForm.id_filtre}
                onChange={handleCategoryChange}
                required
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: 12,
                      boxShadow: '0 4px 16px rgba(15, 23, 42, 0.1)',
                      maxHeight: 300,
                      '& .MuiMenuItem-root': {
                        padding: '12px 20px',
                        fontSize: '0.95rem',
                        fontFamily: "'Inter', sans-serif",
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          background: 'rgba(20, 184, 166, 0.1)',
                        },
                      },
                    },
                  },
                }}
              >
                {availableCategories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </StyledSelect>
              {categoryErrors.id_filtre && (
                <Typography variant="caption" color="error" sx={{ mt: 1, fontFamily: "'Inter', sans-serif" }}>
                  {categoryErrors.id_filtre}
                </Typography>
              )}
            </FormControl>
            <DialogActions sx={{ mt: 4, justifyContent: 'space-between' }}>
              <Button
                onClick={() => setOpenCategoryDialog(false)}
                sx={{
                  color: '#64748b',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  fontFamily: "'Inter', sans-serif",
                  px: 3,
                  py: 1,
                  borderRadius: 8,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'rgba(15, 23, 42, 0.05)',
                    transform: 'scale(1.05)',
                  },
                }}
              >
                Annuler
              </Button>
              <StyledButton type="submit" disabled={actionLoading.submitCategory || categoryLoading}>
                {actionLoading.submitCategory ? (
                  <CircularProgress size={24} sx={{ color: '#fff' }} />
                ) : categoryEditId ? (
                  'Mettre à jour'
                ) : (
                  'Ajouter'
                )}
              </StyledButton>
            </DialogActions>
          </Box>
        </DialogContent>
      </StyledDialog>

      <Snackbar
        open={!!categoryError}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="error"
          sx={{ width: '100%', borderRadius: 12, boxShadow: '0 4px 12px rgba(15, 23, 42, 0.1)', fontFamily: "'Inter', sans-serif" }}
        >
          {categoryError}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!categorySuccess}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{ width: '100%', borderRadius: 12, boxShadow: '0 4px 12px rgba(15, 23, 42, 0.1)', fontFamily: "'Inter', sans-serif" }}
        >
          {categorySuccess}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default memo(CategoriesSection);